import { GenericError } from './genericError.js';

const CONF_ERROR_TYPE = 'ConfigurationError';
/**
 * Ошибка конфигурации. Должна использоваться при конфигурации чего-либо.
 *
 * @example
 * ```js
 * // Проверка конфигурации окружения
 * const confProp = process.env.SOME_CONFIG_PROPERTY;
 *
 * if (!confProp) {
 *  return Err(new ConfigurationError({ message: 'Env value SOME_CONFIG_PROPERTY is empty' }))
 * }
 *
 * return Ok()
 * ```
 *
 * Пример из React
 *
 * ```tsx
 * function AppContext(props) {
 *  const mfRegistry = getMfRegistry();
 *
 *  // В этом случае паникуем, так приложение запустить невозможно
 *  if (!mfRegistry) {
 *    throw new ConfigurationError({ ... });
 *  }
 *
 *  return (<Context.Provider value={{ mfRegistry }}>{props.children}</Context.Provider>)
 * }
 * ```
 *
 * @export
 * @class ConfigurationError
 * @extends {GenericError}
 */
class ConfigurationError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === CONF_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === CONF_ERROR_TYPE;
    }
    type = CONF_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: CONF_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, ConfigurationError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ConfigurationError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const INIT_ERROR_TYPE = 'InitializationError';
/**
 * Ошибка инициализации. Должна использоваться при инициализации чего-либо.
 *
 * @example
 * ```js
 * function init() {
 *  try {
 *    const secret = await readSecretFile();
 *  } catch (err) {
 *    throw new InitializationError({ message: 'Ошибка инициализации', details: err });
 *  }
 * }
 * ```
 *
 * @export
 * @class InitializationError
 * @extends {GenericError}
 */
class InitializationError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === INIT_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === INIT_ERROR_TYPE;
    }
    type = INIT_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: INIT_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, InitializationError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, InitializationError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const NOT_FOUND_ERROR_TYPE = 'NotFoundError';
/**
 * Общий класс ошибок "Не найдено"
 *
 * @example
 *
 * ```ts
 * // Можно использовать как обертку над ошибкой FS ENOENT
 * function readPackageJson(cwd?: string): IResult<PackageJson, NotFoundError>;
 *
 * // Можно использовать как обертку на 404 статусом и 400 ошибкой с данными о том, что что-то не найдено
 * if (statusCode === 404) {
 *  return Err(new NotFoundError({ ..., details: err }));
 * }
 *
 * if (statusCode === 400 && response.err.notFound) {
 *  return Err(new NotFoundError({ ... }))
 * }
 * ```
 *
 * @export
 * @class NotFoundError
 * @extends {GenericError}
 */
class NotFoundError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === NOT_FOUND_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === NOT_FOUND_ERROR_TYPE;
    }
    type = NOT_FOUND_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: NOT_FOUND_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, NotFoundError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, NotFoundError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const UNKNOWN_ERROR_TYPE = 'UnknownError';
/**
 * Ошибка, которая является по своей сути неопределенным поведением. В продуктовом коде ее рекомендуется
 * использовать осторожно (лучше вообще не использовать), только в тех случаях, где хочется подчеркнуть,
 * что код небезопасен
 *
 * @example
 *
 * ```ts
 * // Можно использовать для обертки над сторонними сервисами, такими как GTM или Карты
 * ymaps.addEventListener('error', e => logger.error(new UnknownError({ details: e.error })));
 * ```
 *
 * @export
 * @class UnknownError
 * @extends {GenericError}
 */
class UnknownError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === UNKNOWN_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === UNKNOWN_ERROR_TYPE;
    }
    type = UNKNOWN_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: UNKNOWN_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, UnknownError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, UnknownError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const TIMEOUT_ERROR_TYPE = 'TimeoutError';
/**
 * Общий класс ошибок по таймауту
 *
 * @example
 *
 * ```ts
 * new Promise((res, rej) => {
 *   setTimeout(() => rej(new TimeoutError({})), 1000);
 *
 *   doSomething(res);
 * });
 * ```
 *
 * @export
 * @class TimeoutError
 * @extends {GenericError}
 */
class TimeoutError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === TIMEOUT_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === TIMEOUT_ERROR_TYPE;
    }
    type = TIMEOUT_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: TIMEOUT_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, TimeoutError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, TimeoutError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const VALIDATION_ERROR_TYPE = 'ValidationError';
/**
 * Общий класс ошибок валидации
 *
 * @example
 *
 * ```ts
 * // Можно использовать для валидации входных параметров HTTP-запроса
 * function fooHandler(req: Request): Result<Response, ValidationError> {
 *    if (!req.header('X-RealUserId') || !req.header('X-Foo')) {
 *      return Err(ValidationError({ message: 'Validation Error!', domain: 'fooHandler', details: {
 *        'X-Foo': req.header('X-Foo'),
 *        'X-RealUserId': req.header('X-RealUserId'),
 *      } }));
 *    }
 * }
 *
 * // Ошибку можно наследовать для реализации более узких ошибок валидации
 * class InputPhoneValidationError extends ValidationError {}
 * ```
 *
 * @export
 * @class ValidationError
 * @extends {GenericError}
 */
class ValidationError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === VALIDATION_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === VALIDATION_ERROR_TYPE;
    }
    type = VALIDATION_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: VALIDATION_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, ValidationError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ValidationError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const SERIALIZE_ERROR_TYPE = 'SerializeError';
/**
 * Общий класс ошибок сериализации
 *
 * @example
 *
 * ```ts
 * try {
 *  const result = JSON.stringify(str);
 * } catch (err) {
 *  throw new SerializeError({ ... });
 * }
 * ```
 *
 * @export
 * @class SerializeError
 * @extends {GenericError}
 */
class SerializeError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === SERIALIZE_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === SERIALIZE_ERROR_TYPE;
    }
    type = SERIALIZE_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: SERIALIZE_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, SerializeError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, SerializeError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const DESERIALIZE_ERROR_TYPE = 'DeserializeError';
/**
 * Общий класс ошибок десериализации
 *
 * @example
 *
 * ```ts
 * try {
 *  const result = JSON.parse(str);
 * } catch (err) {
 *  throw new DeserializeError({ ... });
 * }
 * ```
 *
 * @export
 * @class DeserializeError
 * @extends {GenericError}
 */
class DeserializeError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === DESERIALIZE_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === DESERIALIZE_ERROR_TYPE;
    }
    type = DESERIALIZE_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: DESERIALIZE_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, DeserializeError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, DeserializeError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const ABORT_ERROR_TYPE = 'AbortError';
/**
 * Общий класс ошибок для отмены чего либо
 *
 * @example
 *
 * ```ts
 * const ac = new AbortController();
 *
 * setTimeout(() => ac.abort(new AbortError({...})), 1000);
 *
 * await sendHttpRequest(url, { signal: ac.signal });
 * ```
 *
 * @export
 * @class AbortError
 * @extends {GenericError}
 */
class AbortError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === ABORT_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === ABORT_ERROR_TYPE;
    }
    type = ABORT_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: ABORT_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, AbortError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AbortError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}
const LOGIN_ERROR_TYPE = 'LoginError';
/**
 * Общий класс ошибок авторизации
 *
 * @export
 * @class LoginError
 * @extends {GenericError}
 */
class LoginError extends GenericError {
    static is(value) {
        return GenericError.is(value) && value.type === LOGIN_ERROR_TYPE;
    }
    static isLike(value) {
        return GenericError.isLike(value) && Object.getPrototypeOf(value).constructor.name === LOGIN_ERROR_TYPE;
    }
    type = LOGIN_ERROR_TYPE;
    constructor({ message, domain, details }) {
        super({
            type: LOGIN_ERROR_TYPE,
            message,
            domain,
            details,
        });
        Object.setPrototypeOf(this, LoginError.prototype);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, LoginError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
}

export { AbortError, ConfigurationError, DeserializeError, InitializationError, LoginError, NotFoundError, SerializeError, TimeoutError, UnknownError, ValidationError };
//# sourceMappingURL=base.js.map
